import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { type ApiOutputs, ownDevicesSchema } from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { getVisitScope, useVisitScope } from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    ownDevices: {
      queryInterceptors: [
        async (options) => {
          const items: ApiOutputs["ownDevices"]["items"] = [],
            seen = new Set<string>();
          let cursor: string | undefined;
          do {
            options.fnContext.signal.throwIfAborted();
            const page = ownDevicesSchema.parse(
              await options.next({
                ...options,
                input: {
                  query: { limit: "100", ...(cursor ? { cursor } : {}) },
                },
              }),
            );
            if (page.items.length > 100) throw Error("Invalid device page");
            for (const item of page.items) {
              if (seen.has(item.id)) throw Error("Duplicate device");
              seen.add(item.id);
            }
            if (
              page.nextCursor !== null &&
              (!page.items.length ||
                page.nextCursor !== page.items.at(-1)?.id ||
                page.nextCursor === cursor)
            )
              throw Error("Invalid device continuation");
            items.push(...page.items);
            cursor = page.nextCursor ?? undefined;
          } while (cursor);
          return { items, nextCursor: null };
        },
      ],
    },
  },
});
const disabledScope = { uid: "", accountPublicId: "", generation: 0 };
export function useOwnDevices() {
  const scope = useVisitScope();
  const { uid, accountPublicId, generation } = scope ?? disabledScope;
  const query = useQuery(
    orpc.ownDevices.queryOptions({
      queryKey: ["device-reads", uid, accountPublicId, generation],
      input: { query: { limit: "100" } },
      context: { expectedUserUid: scope?.uid },
      enabled: !!scope,
      staleTime: 30_000,
      retry: false,
    }),
  );
  const refetch = query.refetch;
  const refresh = useCallback(async () => {
    if (scope && getVisitScope() === scope) await refetch();
  }, [scope, refetch]);
  return {
    devices: scope && query.isSuccess ? query.data.items : [],
    scope,
    ready: !!scope && query.isSuccess,
    loading: !!scope && query.isPending,
    error: !!scope && query.isError,
    refresh,
  };
}
